import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Row,
  Typography,
  Card,
} from 'antd';
import classes from './ShareModal.module.scss';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Assets from 'assets/icons';
import { RangePickerProps } from 'antd/es/date-picker';
import { ExpireTypes, ModeTypes } from './types';

const PASSWORD_LENGTH = 6;

type ShareFormProps = {
  form: any;
  onFinish: (values: any) => void;
  expires: ExpireTypes;
  setExpires: (value: ExpireTypes) => void;
  isPasswordEnabled: boolean;
  setIsPasswordEnabled: (value: boolean) => void;
};

export default function ShareForm({
  form,
  onFinish,
  expires,
  setExpires,
  isPasswordEnabled,
  setIsPasswordEnabled,
}: ShareFormProps) {
  const { t } = useTranslation();

  const availableModes = [
    { value: ModeTypes.OnlyView, label: t('Documents.modals.share.viewMode') },
    { value: ModeTypes.All, label: t('Documents.modals.share.allMode') },
  ];

  const availableExpires = [
    {
      value: ExpireTypes.Count,
      label: t('Documents.modals.share.countExpires'),
    },
    { value: ExpireTypes.Time, label: t('Documents.modals.share.timeExpires') },
  ];

  const defaultValues = {
    mode: ModeTypes.OnlyView,
    expires_option: ExpireTypes.Count,
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current < dayjs().subtract(1, 'day').endOf('day');
  };

  const CountPicker = (
    <Form.Item
      rules={[
        { required: true, message: t('Documents.modals.share.validateNumber') },
      ]}
      label={t('Documents.modals.share.countLabel')}
      name={ExpireTypes.Count}
      style={{
        display: 'inline-block',
        width: 'calc(40% - 15px)',
        margin: '0 0 0 15px',
      }}
    >
      <InputNumber
        min={1}
        max={1000}
        placeholder={t('Documents.modals.share.countPlacrholder')}
        style={{ width: '100%' }}
      />
    </Form.Item>
  );

  const TimePicker = (
    <Form.Item
      rules={[{ required: true }]}
      label={t('Documents.modals.share.timeLabel')}
      name={ExpireTypes.Time}
      style={{
        display: 'inline-block',
        width: 'calc(40% - 15px)',
        margin: '0 0 0 15px',
      }}
    >
      <DatePicker
        disabledDate={disabledDate}
        showTime
        format={'HH:mm DD-MM-YYYY'}
        placeholder={t('Documents.modals.share.timePlacrholder')}
        style={{ width: '100%' }}
      />
    </Form.Item>
  );

  return (
    <Form
      form={form}
      initialValues={defaultValues}
      layout='vertical'
      onFinish={onFinish}
      className={classes.form}
      onChange={(e: any) => {
        e.target.id === ExpireTypes.Count &&
          form.validateFields([ExpireTypes.Count]);
      }}
    >
      <Form.Item
        dependencies={['email']}
        rules={[
          { whitespace: true },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (getFieldValue('email') && !value?.trim()) {
                return Promise.reject(new Error(t('Documents.modals.share.validateUserNameRequired')));
              }
              return Promise.resolve();
            },
          }),
        ]}
        label={t('Documents.modals.share.userNameLabel')}
        name='user_name'
        className={classes.item}
      >
        <Input placeholder={t('Documents.modals.share.userNamePlaceholder')} />
      </Form.Item>

      <Form.Item
        dependencies={['user_name']}
        rules={[
          { whitespace: true },
          { type: 'email' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (getFieldValue('user_name')?.trim() && !value) {
                return Promise.reject(new Error(t('Documents.modals.share.validateEmailRequired')));
              }
              return Promise.resolve();
            },
          }),
        ]}
        label={t('Documents.modals.share.emailLabel')}
        name='email'
        className={classes.item}
        validateTrigger='onChange'
      >
        <Input placeholder={t('Documents.modals.share.emailPlaceholder')} />
      </Form.Item>

      <Form.Item name='mode' label={t('Documents.modals.share.chooseMode')}>
        <Select options={availableModes} />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <Form.Item
          label={t('Documents.modals.share.chooseExpires')}
          name='expires_option'
          style={{ display: 'inline-block', width: '60%' }}
        >
          <Select options={availableExpires} onChange={setExpires} />
        </Form.Item>

        {expires === ExpireTypes.Count ? CountPicker : TimePicker}
      </Form.Item>

      <Card
        size='default'
        title={
          <Row align='middle' style={{ gap: 4 }}>
            <Assets.LockIcon className={classes.passwordProtectionIcon} />
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {t('Documents.modals.share.passwordProtection')}
            </Typography.Title>
          </Row>
        }
        extra={
          <Switch
            checkedChildren='ON'
            unCheckedChildren='OFF'
            checked={isPasswordEnabled}
            onChange={setIsPasswordEnabled}
            size='default'
          />
        }
      >
        <Typography.Text
          type='secondary'
          className={classes.passwordProtectionDescription}
        >
          {t('Documents.modals.share.passwordDescription')}
        </Typography.Text>

        <Form.Item
          rules={
            isPasswordEnabled
              ? [
                  {
                    required: true,
                    message: t('Documents.modals.share.validatePassword'),
                  },
                  {
                    len: PASSWORD_LENGTH,
                    message: t(
                      'Documents.modals.share.validatePasswordLength',
                      { len: PASSWORD_LENGTH }
                    ),
                  },
                ]
              : []
          }
          label={
            isPasswordEnabled ? t('Documents.modals.share.passwordLabel') : ''
          }
          name='password'
          className={`${classes.passwordFieldContainer} ${
            isPasswordEnabled ? classes.visible : classes.hidden
          }`}
        >
          <Input.Password 
            placeholder={t('Documents.modals.share.passwordPlaceholder')}
            autoComplete='one-time-code'
            disabled={!isPasswordEnabled}
            maxLength={PASSWORD_LENGTH}
          />
        </Form.Item>
      </Card>
    </Form>
  );
}
